import { z } from "zod";
import { EmailDeliverySchema } from "./emailDelivery";

// A kit line's component snapshot: what the sale pulls off the shelf and
// (optionally) lists on the customer document — component prices never print;
// the kit sells at its one price. Quantities are per ONE kit.
export const LineComponentSchema = z.object({
  itemId: z.string().nullable().default(null),
  name: z.string().max(200),
  quantity: z.number().positive().max(100000),
});
export type LineComponent = z.infer<typeof LineComponentSchema>;

export const LineItemSchema = z.object({
  description: z.string().min(1).max(500),
  quantity: z.number().positive().max(100000),
  // Negative = a discount/adjustment line (and FieldPulse imports carry them).
  unitPriceCents: z.number().int().min(-100000000).max(100000000),
  unitCostCents: z.number().int().nonnegative().nullable().default(null),
  taxable: z.boolean().default(true),
  // Set when the line came from the pricebook — the hook inventory hangs on.
  itemId: z.string().nullable().default(null),
  // "hide" prints the line without unit price/amount on customer documents
  // (the total still shows). Counts toward totals either way.
  priceVisibility: z.enum(["show", "hide"]).default("show"),
  // Present on kit lines: snapshotted at add time so later kit edits never
  // rewrite history. Inventory decrements components, never the kit itself.
  components: z.array(LineComponentSchema).max(50).nullable().default(null),
  showComponents: z.boolean().default(true),
  /** The customer's maintenance-plan discount, kept in step by the server (one line, recomputed on every save). */
  memberDiscount: z.boolean().optional(),
  // Already taken from stock when the tech logged it on the job: sending the
  // invoice must not move inventory again.
  stockPulled: z.boolean().optional(),
  /** The pricebook item's picture, shown to the customer next to the line. */
  photoUrl: z.string().max(2000).optional(),
});
export type LineItem = z.infer<typeof LineItemSchema>;

// Few statuses, one direction, no scrolling pill strips.
export const EstimateStatusSchema = z.enum(["draft", "sent", "accepted", "declined"]);
export type EstimateStatus = z.infer<typeof EstimateStatusSchema>;

// written_off: the rest won't be collected (comped, uncollectible). It stays a real invoice,
// with whatever was paid, and comes off what's owed; it can be undone.
export const InvoiceStatusSchema = z.enum(["draft", "sent", "partially_paid", "paid", "void", "written_off"]);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const PaymentRecordSchema = z.object({
  amountCents: z.number().int().positive(),
  method: z.enum(["card", "ach", "check", "cash", "other"]).default("other"),
  note: z.string().max(500).default(""),
  recordedByUid: z.string(),
  at: z.number(),
  /** Taken as the estimate's deposit (it moves onto the invoice with it). */
  deposit: z.boolean().optional(),
});
export type PaymentRecord = z.infer<typeof PaymentRecordSchema>;

/** What the customer pays up front when they sign: a percent of the total or a fixed amount. */
export const DepositSchema = z.object({
  kind: z.enum(["percent", "fixed"]),
  /** percent: basis points (2500 = 25%); fixed: cents. */
  value: z.number().int().nonnegative().max(100000000),
});
export type Deposit = z.infer<typeof DepositSchema>;

export function depositCents(d: Deposit | null | undefined, totalCents: number): number {
  if (!d || d.value <= 0 || totalCents <= 0) return 0;
  const cents = d.kind === "percent" ? Math.round((totalCents * Math.min(d.value, 10000)) / 10000) : d.value;
  return Math.min(cents, totalCents);
}

const moneyDocBase = {
  number: z.number().int().positive(), // sequential per tenant
  customerId: z.string().nullable().default(null),
  customerName: z.string().default(""),
  jobId: z.string().nullable().default(null),
  // The builder's development this document belongs to, when there is one.
  projectId: z.string().nullable().optional(),
  lines: z.array(LineItemSchema).min(1).max(200),
  taxRateBps: z.number().int().min(0).max(5000).default(0), // basis points, e.g. 775 = 7.75%
  // Totals are computed server-side from lines; never trusted from clients.
  subtotalCents: z.number().int().nonnegative(),
  taxCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  notes: z.string().max(5000).default(""),
  /** Terms printed on the document (from its type, or the company's invoice terms). */
  terms: z.string().max(20000).optional(),
  /** The estimate owner: who sold it (commissions). Carried onto the invoice. */
  salespersonUid: z.string().nullable().optional(),
  salespersonName: z.string().optional(),
  /** The estimate type it was made as (Commercial, Service…). */
  typeName: z.string().max(80).optional(),
  /** Who pressed Send (finalize). */
  sentByUid: z.string().optional(),
  /** Every time it was emailed (newest last), written by the email function. */
  emails: z
    .array(
      z.object({
        at: z.number(),
        byName: z.string(),
        to: z.array(z.string()),
        cc: z.array(z.string()).default([]),
        bcc: z.array(z.string()).default([]),
        /** Company documents attached (names). */
        docs: z.array(z.string()).default([]),
        ok: z.boolean(),
        error: z.string().default(""),
        /** The email service's id (delivery news is keyed by it). */
        id: z.string().nullable().optional(),
      }),
    )
    .optional(),
  /** Delivery news per email id (delivered, opened, bounced…). */
  emailDelivery: z.record(z.string(), EmailDeliverySchema).optional(),
  createdByUid: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
};

/** A maintenance-plan discount applied to a document (the line itself is in `lines`, flagged memberDiscount). */
export const MemberDiscountSchema = z.object({
  agreementId: z.string(),
  name: z.string().default(""),
  bps: z.number().int().min(0).max(10000),
  /** auto: kept in step with the lines. off: the office took it off this document. */
  mode: z.enum(["auto", "off"]).default("auto"),
});
export type MemberDiscount = z.infer<typeof MemberDiscountSchema>;

export const EstimateSchema = z.object({
  ...moneyDocBase,
  status: EstimateStatusSchema.default("draft"),
  sentAt: z.number().nullable().default(null),
  decidedAt: z.number().nullable().default(null),
  invoiceId: z.string().nullable().default(null), // set on conversion
  expiresAt: z.number().nullable().default(null),
  // On-site approval: the homeowner signs on the rep's device.
  signerName: z.string().max(200).nullable().default(null),
  signaturePngBase64: z.string().max(400000).nullable().default(null),
  signedAt: z.number().nullable().default(null),
  /** Signed on the rep's device, or from the emailed link. */
  signedVia: z.enum(["onsite", "link"]).optional(),
  /** The emailed sign link's secret (made when the estimate is sent). */
  signToken: z.string().optional(),
  typeId: z.string().nullable().optional(),
  memberDiscount: MemberDiscountSchema.nullable().optional(),
  deposit: DepositSchema.nullable().optional(),
  /** Deposit payments taken before there's an invoice; they move onto it. */
  depositPayments: z.array(PaymentRecordSchema).optional(),
  depositPaidCents: z.number().int().nonnegative().optional(),
  /** What the customer said when they declined online. */
  declineNote: z.string().max(1000).optional(),
  /** Who marked it accepted or declined by hand (not the customer's own signing). */
  decidedByUid: z.string().optional(),
});
export type Estimate = z.infer<typeof EstimateSchema>;

// Estimate types (Commercial, Service, Residential…): each with its own terms,
// how long the price holds, and the deposit it asks for. Admins set them
// on the Estimates page; a new estimate starts from one.
export const EstimateTypeSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().trim().min(1).max(80),
  terms: z.string().max(20000).default(""),
  validDays: z.number().int().positive().max(365).nullable().default(null),
  deposit: DepositSchema.nullable().default(null),
});
export type EstimateType = z.infer<typeof EstimateTypeSchema>;

export const SalesSettingsSchema = z.object({
  estimateTypes: z.array(EstimateTypeSchema).max(20).default([]),
  /** Printed on every invoice unless the invoice has its own. */
  invoiceTerms: z.string().max(20000).default(""),
  /**
   * Commission on the sale before tax, on the margin (sale minus cost), or on
   * parts only (lines that carry a cost; labor and fees don't). The company's
   * choice, like everything about commissions here.
   */
  commissionBasis: z.enum(["subtotal", "margin", "parts"]).default("subtotal"),
  /** Earned when the invoice is sent, or as the customer pays (each payment's share). */
  commissionWhen: z.enum(["invoiced", "collected"]).default("collected"),
  /** Default rate per employee type (role), basis points. A person's own rate on Team wins. */
  commissionDefaults: z.record(z.string(), z.number().int().min(0).max(10000)).default({}),
  /** Maintenance agreement terms by department id ("" = any department). */
  agreementTerms: z.record(z.string(), z.string().max(20000)).default({}),
  /** A processing fee added when a customer pays by card (basis points; 300 = 3%). 0 = none. Disclosed on documents and at checkout. */
  cardFeeBps: z.number().int().min(0).max(400).default(0),
  updatedAt: z.number().default(0),
});
export type SalesSettings = z.infer<typeof SalesSettingsSchema>;


export const InvoiceSchema = z.object({
  ...moneyDocBase,
  status: InvoiceStatusSchema.default("draft"),
  estimateId: z.string().nullable().default(null),
  memberDiscount: MemberDiscountSchema.nullable().optional(),
  sentAt: z.number().nullable().default(null),
  dueAt: z.number().nullable().default(null),
  amountPaidCents: z.number().int().nonnegative().default(0),
  payments: z.array(PaymentRecordSchema).default([]),
  // The customer's signature on the finished invoice (on the tech's or office's device).
  signerName: z.string().max(200).nullable().optional(),
  signaturePngBase64: z.string().max(400000).nullable().optional(),
  signedAt: z.number().nullable().optional(),
  signedByUid: z.string().optional(),
  voidedAt: z.number().optional(),
  voidedByUid: z.string().optional(),
  /** Written off: the unpaid balance at the time, why, who, and the status to go back to on undo. */
  writeOff: z
    .object({
      amountCents: z.number().int().nonnegative(),
      note: z.string().max(500),
      at: z.number(),
      byUid: z.string(),
      byName: z.string(),
      previousStatus: z.enum(["sent", "partially_paid"]),
    })
    .nullable()
    .optional(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

// Flat-rate pricebook entry — FieldPulse's best-rated idea, without the
// three-screens-deep drill-down.
export const PricebookItemSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(1000).default(""),
  category: z.string().max(100).default(""),
  // Kits ("Fireplace setup"): bundled items sold as ONE line at one price.
  // Costs roll up from components for profitability; selling a kit decrements
  // each component's stock, never a stock count on the kit itself.
  kind: z.enum(["item", "kit"]).default("item"),
  components: z
    .array(z.object({ itemId: z.string(), name: z.string().max(200).default(""), quantity: z.number().positive().max(100000) }))
    .max(50)
    .default([]),
  // List the included items (names only, no prices) on customer documents.
  showComponents: z.boolean().default(true),
  sku: z.string().max(80).default(""), // scannable; promoted from fp.sku on import
  /** A picture of it (download URL), shown in the pricebook and to customers on estimates. */
  photoUrl: z.string().max(2000).optional(),
  priceCents: z.number().int().nonnegative(),
  costCents: z.number().int().nonnegative().nullable().default(null),
  /** What a tech earns per unit installed on a piece-rate job (office-only). */
  pieceRateCents: z.number().int().nonnegative().nullable().optional(),
  taxable: z.boolean().default(true),
  // Inventory: opt-in per item. Quantities move through server paths
  // (adjust callable, receiving, invoice send) so the movements log stays true.
  trackInventory: z.boolean().default(false),
  /** In the yard. */
  onHandQty: z.number().default(0),
  /** Loaded on trucks for jobs, not used or back yet (stockLoads). */
  onTruckQty: z.number().default(0),
  reorderLevel: z.number().nullable().default(null),
  bin: z.string().max(60).default(""),
  archivedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});

// One row per quantity change, server-written: the inventory audit trail.
export const InventoryMovementSchema = z.object({
  itemId: z.string(),
  itemName: z.string().default(""),
  delta: z.number(),
  onHandAfter: z.number(),
  /** Trucks: the change to what's loaded, and the loaded count after. */
  truckDelta: z.number().optional(),
  onTruckAfter: z.number().optional(),
  reason: z.enum(["adjust", "receive", "invoice", "void", "import", "job", "load", "return"]),
  refType: z.enum(["job", "invoice", "orderedItem", "purchaseOrder"]).nullable().default(null),
  refId: z.string().nullable().default(null),
  note: z.string().max(500).default(""),
  actorUid: z.string().default(""),
  actorName: z.string().default(""),
  at: z.number(),
});
export type InventoryMovement = z.infer<typeof InventoryMovementSchema>;

// Who you buy from.
export const VendorSchema = z.object({
  name: z.string().min(1).max(160),
  accountNumber: z.string().max(80).default(""),
  phone: z.string().max(40).default(""),
  email: z.string().max(200).default(""),
  note: z.string().max(2000).default(""),
  archivedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Vendor = z.infer<typeof VendorSchema>;
export type PricebookItem = z.infer<typeof PricebookItemSchema>;

export const COMMISSION_BASIS_LABEL: Record<SalesSettings["commissionBasis"], string> = {
  subtotal: "the sale, before tax",
  margin: "the margin (sale minus cost)",
  parts: "parts only (lines that carry a cost)",
};

type CommissionLine = { quantity: number; unitPriceCents?: number; unitCostCents: number | null };

/** What a commission is figured on for one invoice (before the rate). */
export function commissionBaseCents(inv: { subtotalCents: number; lines: CommissionLine[] }, basis: SalesSettings["commissionBasis"]): number {
  if (basis === "subtotal") return Math.max(0, inv.subtotalCents);
  if (basis === "parts") {
    // A part carries a cost; labor, service calls and fees don't.
    return Math.max(0, inv.lines.filter((l) => (l.unitCostCents ?? 0) > 0).reduce((s, l) => s + Math.round(l.quantity * (l.unitPriceCents ?? 0)), 0));
  }
  const cost = inv.lines.reduce((s, l) => s + Math.round(l.quantity * (l.unitCostCents ?? 0)), 0);
  return Math.max(0, inv.subtotalCents - cost);
}

/** A person's rate: their own (set on Team) first — 0 there means none — else their employee type's default, else none. */
export function commissionRateFor(own: number | null | undefined, role: string | null | undefined, defaults: Record<string, number>): number {
  if (own !== null && own !== undefined) return own;
  return (role && defaults[role]) || 0;
}

/**
 * The part of an invoice's commission base earned in [start, end):
 * "invoiced" — all of it, if the invoice was sent in the range;
 * "collected" — each payment's share of it (a 50% payment earns half).
 */
export function commissionEarnedCents(
  inv: { status: string; sentAt: number | null; totalCents: number; subtotalCents: number; lines: CommissionLine[]; payments: Array<{ amountCents: number; at: number }> },
  basis: SalesSettings["commissionBasis"],
  when: SalesSettings["commissionWhen"],
  start: number,
  end: number,
): number {
  if (inv.status === "void" || inv.status === "draft") return 0;
  const base = commissionBaseCents(inv, basis);
  if (when === "invoiced") return inv.sentAt !== null && inv.sentAt >= start && inv.sentAt < end ? base : 0;
  if (inv.totalCents <= 0) return 0;
  const paid = inv.payments.filter((p) => p.at >= start && p.at < end).reduce((s, p) => s + p.amountCents, 0);
  return Math.round((base * Math.min(paid, inv.totalCents)) / inv.totalCents);
}

export function computeTotals(lines: LineItem[], taxRateBps: number) {
  let subtotal = 0;
  let taxable = 0;
  for (const l of lines) {
    const amount = Math.round(l.quantity * l.unitPriceCents);
    subtotal += amount;
    if (l.taxable) taxable += amount;
  }
  const tax = Math.round((taxable * taxRateBps) / 10000);
  return { subtotalCents: subtotal, taxCents: tax, totalCents: subtotal + tax };
}
